/* Intelligence Plan derivation (W1-C) — the pure state→plan projection the
 * #/client/{slug}/plan screen renders. Anatomy §4.4(4)/§5.2/§5.3 is the binding
 * semantics; the design export's Strategy screen is the visual grammar.
 *
 * HONESTY SPINE — the payload's Opportunity contract is deliberately minimal:
 *   { platform, action, rationale, peersPresent, priority }
 * It carries NO title, NO horizon, NO confidence, NO contract-scope disposition
 * and NO stable id. So this module NEVER fabricates any of them:
 *   • title       → reused from the single shared opportunityTitle() source.
 *   • horizon     → null (the plan renders "no horizon assigned", never 30/90/180).
 *   • confidence  → null (rendered only if a future contract carries it).
 *   • scope       → null (no in-contract/upsell field exists — never invented).
 *   • evidence    → the REAL mapped-asset link(s) for the opportunity's platform,
 *                   or an honest "no evidence links recorded". Nothing invented.
 * The stable action key `opp:{action}:{slugify(platform)[:24]}` is the SAME key
 * service/tools/ws_derive.py::derive_actions emits, so the view can match an
 * opportunity to its real ledger task (seeded at run reconcile) — wording- and
 * count-independent, surviving re-runs.
 *
 * DECISIONS: there is NO opportunity-decision store in the backend (verified —
 * no decisions table in wsdb.py; task statuses are TASK state, not the
 * accept/defer/dismiss DECISION lifecycle of anatomy §5.3). This module records
 * that reality (`decision: null`); the view renders the decision controls
 * disabled with an honest "not wired" note, never a client-side-only fake.
 *
 * Pure + unit-tested (tests/unit/plan.test.ts) against the el-al and ascensus
 * fixtures. No DOM reads, no WS access (the live task match happens in the view).
 */
import { slugify } from '../lib/format.ts';
import { opportunityTitle } from './actions.ts';
import { entityHeadline } from './entity.ts';
import { coveragePanel } from './coverage.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { NormalizedAsset, Opportunity, OpportunityAction, Priority } from '../types/contract.ts';

/** A real evidence receipt for an opportunity — a mapped-asset link the payload
 * genuinely carries (never fabricated). */
export interface PlanEvidence {
  label: string;
  url: string;
}

/** One opportunity as the plan renders it — every field either sourced from the
 * payload or explicitly null (honest absence). */
export interface PlanOpportunity {
  /** 1-based rank in the priority-ordered plan. */
  rank: number;
  /** display label, e.g. "OPP-1". */
  rankLabel: string;
  action: OpportunityAction;
  platform: string;
  /** the shared human title ("Claim — TripAdvisor"), never the bare word. */
  title: string;
  rationale: string;
  priority: Priority;
  /** the ledger task key this opportunity maps to (`opp:{action}:{slug}`) — the
   * view resolves it against the live WS board. */
  actionKey: string;
  /** real mapped-asset receipts, or [] when none are derivable. */
  evidence: PlanEvidence[];
  /** comparator peers named in the payload (rarely populated). */
  peersPresent: string[];
  /** true when derive_actions folds this (Wikidata) opportunity into the single
   * `wikidata-worksheet` task instead of emitting `actionKey` — so the view
   * points the linked-work lookup at the worksheet, honestly. */
  foldedIntoWorksheet: boolean;
  /** the strategic horizon — the payload carries none, so ALWAYS null here. */
  horizon: null;
  /** model confidence — the payload carries none, so ALWAYS null here. */
  confidence: null;
  /** in-contract vs upsell — the payload carries no disposition, ALWAYS null. */
  scopeDisposition: null;
}

/** One honest research gap surfaced on the plan banner + gap card. */
export interface PlanGap {
  label: string;
  note: string;
}

/** The plan's coverage state — the banner's chip + headline + honest gaps. */
export interface PlanCoverage {
  /** true when the run carries reconciled phase-keyed coverage (coveragePanel).
   * false when only raw/legacy coverage keys exist (e.g. the ascensus fixture's
   * camelCase keys) — the per-layer counts are then honestly unavailable. */
  available: boolean;
  /** 'ready' only when every in-scope layer is checked AND none thin (anatomy
   * §5.2 — a not-run/thin layer makes a plan 'partial', never ready);
   * 'unknown' when coverage isn't phase-keyed. */
  chip: 'ready' | 'partial' | 'unknown';
  /** the one-line coverage headline. */
  headline: string;
  checkedCount: number | null;
  inScopeTotal: number | null;
  thinCount: number | null;
  scopedCount: number | null;
  /** unchecked or thin layers, named — a real, explicit research-gap list
   * (scanned from the raw coverage notes so it works for either key style). */
  gaps: PlanGap[];
}

/** Run identity for the plan banner/head — real stamps only; version honestly
 * absent (the payload carries no plan-version number). */
export interface PlanIdentity {
  generatedAt: string | null;
  /** YYYY-MM-DD, from generatedAt or the run archive filename, else null. */
  dateLabel: string | null;
  runFile: string | null;
  hasSynthesis: boolean;
  /** the payload carries no plan version — always false, so the view never
   * prints "version N". */
  versionKnown: false;
  /** deliberate scope note (a profile-scoped run skipped layers), else null. */
  scopeNote: string | null;
}

const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

/** The exact ledger key derive_actions() emits for an opportunity. */
export function opportunityKey(o: { action: OpportunityAction; platform: string }): string {
  return `opp:${o.action}:${slugify(o.platform).slice(0, 24)}`;
}

/** Real evidence receipts for an opportunity: the mapped asset(s) for its
 * platform that carry a URL. Mirrors the platform→asset match probeFor() uses,
 * so an opportunity's receipts are the same properties the rest of the hub links
 * to. Never fabricates a URL. */
function evidenceFor(state: AppPayloadState, o: Opportunity): PlanEvidence[] {
  const platform = (o.platform || '').trim().toLowerCase();
  if (!platform) return [];
  const out: PlanEvidence[] = [];
  const seen = new Set<string>();
  for (const a of state.assets as NormalizedAsset[]) {
    if (!a.url || seen.has(a.url)) continue;
    const ap = (a.platform || '').toLowerCase();
    const at = (a.title || '').toLowerCase();
    if (ap === platform || at.includes(platform) || (ap && platform.includes(ap))) {
      seen.add(a.url);
      out.push({ label: a.title || a.platform || a.url, url: a.url });
      if (out.length >= 4) break;
    }
  }
  return out;
}

/** The priority-ordered plan opportunities. Stable: priority, then platform, so
 * a re-run with the same opportunities produces the same ranks/keys. */
export function planOpportunities(state: AppPayloadState): PlanOpportunity[] {
  const eh = entityHeadline(state);
  const wikidataFolds = !!(eh && eh.fix > 0);
  const items = [...(state.opportunities || [])];
  items.sort((a, b) =>
    (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority])
    || (a.platform || '').localeCompare(b.platform || ''));
  return items.map((o, i) => {
    const isWikidata = /wikidata/i.test(o.platform || '');
    return {
      rank: i + 1,
      rankLabel: `OPP-${i + 1}`,
      action: o.action,
      platform: o.platform || '',
      title: opportunityTitle(o),
      rationale: o.rationale || '',
      priority: o.priority || 'medium',
      actionKey: opportunityKey(o),
      evidence: evidenceFor(state, o),
      peersPresent: o.peersPresent || [],
      foldedIntoWorksheet: isWikidata && wikidataFolds,
      horizon: null,
      confidence: null,
      scopeDisposition: null,
    };
  });
}

// A coverage note that reads as a not-run / deferred / skipped gap (as opposed
// to a substantive completion note). Works on either key style's prose value.
const GAP_NOTE = /^\s*(deferred|not\s*run|no\s+dedicated|skipped|light in stage|n\/a|none\b|budget-limited|pending)/i;

/** Humanize a coverage key (phase1_owned_editable | ownedEditable) to a label. */
function coverageLabel(key: string): string {
  const body = key
    .replace(/^phase\d+_?/, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b(impact|aiq|ai|kp)\b/gi, (m) => m.toUpperCase())
    .trim();
  return body.replace(/^./, (c) => c.toUpperCase()) || key;
}

/** Scan the raw coverage notes for explicit research gaps (not-run / deferred /
 * skipped layers). Key-style-independent, so it names gaps for BOTH the
 * phase-keyed (el-al) and camelCase (ascensus) runs. */
function coverageGaps(state: AppPayloadState): PlanGap[] {
  const gaps: PlanGap[] = [];
  for (const [key, raw] of Object.entries(state.coverage || {})) {
    if (/^(synthesis|synthesis_phase)$/i.test(key)) continue;
    const v = String(raw ?? '').trim();
    if (!v || !GAP_NOTE.test(v)) continue;
    // first clause of the note, trimmed, as the honest reason
    const note = v.split(/[.;—]/)[0]?.trim() || v;
    gaps.push({ label: coverageLabel(key), note });
  }
  return gaps;
}

/** The plan coverage state for the banner. Prefers the reconciled phase-keyed
 * panel (counts); falls back to an honest "not phase-keyed" state otherwise. The
 * gap list is derived independently of key style, so it is populated either way. */
export function planCoverage(state: AppPayloadState): PlanCoverage {
  const panel = coveragePanel(state);
  if (!panel) {
    // No reconciled phase-keyed panel (e.g. legacy camelCase coverage keys):
    // fall back to the raw-note scan for gaps — the ONLY honest source here.
    const gaps = coverageGaps(state);
    const rawCount = Object.keys(state.coverage || {}).length;
    return {
      available: false,
      chip: 'unknown',
      headline: rawCount
        ? `Per-layer coverage is not in the reconciled phase-keyed form on this run — ${rawCount} coverage note${rawCount === 1 ? '' : 's'} recorded, counts not computed`
        : 'No coverage accounting on this run',
      checkedCount: null,
      inScopeTotal: null,
      thinCount: null,
      scopedCount: null,
      gaps,
    };
  }
  // Panel available: derive gaps FROM the reconciled layers so a layer the panel
  // backfilled via the IMPACT/AIQ enrichment plane (Search/AI) is never listed
  // as a gap while the headline counts it observed — the banner can't contradict
  // itself. Only in-scope, unchecked-or-thin layers are gaps.
  const gaps: PlanGap[] = panel.layers
    .filter((l) => !l.scoped && (!l.checked || l.thin))
    .map((l) => ({ label: l.label, note: !l.checked ? 'not run on this run' : (l.thinNote || 'flagged thin') }));
  const parts = [`${panel.checkedCount} of ${panel.inScopeTotal} in-scope layers observed`];
  if (panel.thinCount) parts.push(`${panel.thinCount} thin`);
  if (panel.scopedCount) parts.push(`${panel.scopedCount} scoped out`);
  return {
    available: true,
    chip: panel.clean ? 'ready' : 'partial',
    headline: parts.join(' · '),
    checkedCount: panel.checkedCount,
    inScopeTotal: panel.inScopeTotal,
    thinCount: panel.thinCount,
    scopedCount: panel.scopedCount,
    gaps,
  };
}

/** Extract a YYYY-MM-DD from a run archive filename (…-YYYYMMDD-HHMMSS-…). */
function dateFromRunFile(runFile: string | null): string | null {
  if (!runFile) return null;
  const m = runFile.match(/(\d{4})(\d{2})(\d{2})-\d{6}/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** Real run identity for the plan head/banner — version honestly absent. */
export function planIdentity(state: AppPayloadState): PlanIdentity {
  const dateLabel = (state.generatedAt ? state.generatedAt.slice(0, 10) : null)
    || dateFromRunFile(state.runFile);
  const skipped = (state.skippedLayers || []).length;
  return {
    generatedAt: state.generatedAt,
    dateLabel,
    runFile: state.runFile,
    hasSynthesis: !!(state.synthesis && state.synthesis.executiveNarrative?.length),
    versionKnown: false,
    scopeNote: skipped
      ? `Profile-scoped run — ${skipped} layer${skipped === 1 ? '' : 's'} deliberately not checked`
      : null,
  };
}
